import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function injectScript(id: string, json: object) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(json);
  document.head.appendChild(script);
}

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '黄本子官方指南',
  url: 'https://www.huangbenzi.com',
  description: '黄本子官方指南致力于为用户提供全面、准确、及时的黄本子相关信息',
  email: 'contact@huangbenzi.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: '北京',
    addressCountry: 'CN',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-XXXX',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '黄本子官方指南',
  url: 'https://www.huangbenzi.com',
  description: '最权威、最详尽的黄本子专题落地页，全面解析黄本子的定义、用途、获取方式及注意事项',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.huangbenzi.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

export function getWebPageSchema(url: string, name: string, description: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: `https://www.huangbenzi.com${url}`,
    name,
    description,
    isPartOf: {
      '@type': 'WebSite',
      name: '黄本子官方指南',
      url: 'https://www.huangbenzi.com',
    },
    about: {
      '@type': 'Thing',
      name: '黄本子',
    },
    inLanguage: 'zh-Hans',
  };
}

export function getBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      item: `https://www.huangbenzi.com${item.url}`,
    })),
  };
}

export function getArticleSchema(
  title: string,
  description: string,
  url: string,
  datePublished: string,
  category: string
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: `https://www.huangbenzi.com${url}`,
    datePublished,
    author: {
      '@type': 'Organization',
      name: '黄本子官方指南',
    },
    publisher: {
      '@type': 'Organization',
      name: '黄本子官方指南',
    },
    articleSection: category,
    about: {
      '@type': 'Thing',
      name: '黄本子',
    },
    inLanguage: 'zh-Hans',
  };
}

export function getFAQSchema(
  faqs: { question: string; answer: string }[]
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

interface SchemaOrgProps {
  type: 'home' | 'category' | 'article' | 'about';
  categoryName?: string;
  articleTitle?: string;
  articleDescription?: string;
  articleDate?: string;
  articleCategory?: string;
  faqs?: { question: string; answer: string }[];
}

export default function SchemaOrg({
  type,
  categoryName,
  articleTitle,
  articleDescription,
  articleDate,
  articleCategory,
  faqs,
}: SchemaOrgProps) {
  const location = useLocation();

  useEffect(() => {
    injectScript('ld-org', organizationSchema);
    injectScript('ld-website', websiteSchema);

    const pageNameMap: Record<string, string> = {
      home: '黄本子官方指南 - 首页',
      category: `${categoryName || '分类'} - 黄本子官方指南`,
      article: `${articleTitle || '文章'} - 黄本子官方指南`,
      about: '关于我们 - 黄本子官方指南',
    };

    const pageDescMap: Record<string, string> = {
      home: '黄本子是什么？全面解析黄本子的定义、用途、获取方式及注意事项，最权威的黄本子专题落地页。',
      category: `浏览黄本子${categoryName || ''}相关的全部文章，获取最新最全的信息指南。`,
      article: articleDescription || '黄本子专题文章，深度解析相关知识。',
      about: '了解黄本子官方指南的品牌故事、团队背景和联系方式。',
    };

    injectScript(
      'ld-webpage',
      getWebPageSchema(location.pathname, pageNameMap[type] || pageNameMap.home, pageDescMap[type] || pageDescMap.home)
    );

    if (type === 'home') {
      const breadcrumbItems = [{ name: '首页', url: '/' }];
      injectScript('ld-breadcrumb', getBreadcrumbSchema(breadcrumbItems));
      if (faqs && faqs.length > 0) {
        injectScript('ld-faq', getFAQSchema(faqs));
      }
    }

    if (type === 'category' && categoryName) {
      const breadcrumbItems = [
        { name: '首页', url: '/' },
        { name: categoryName, url: location.pathname },
      ];
      injectScript('ld-breadcrumb', getBreadcrumbSchema(breadcrumbItems));
    }

    if (type === 'article' && articleTitle && categoryName) {
      const breadcrumbItems = [
        { name: '首页', url: '/' },
        { name: categoryName, url: `/category/${articleCategory || ''}` },
        { name: articleTitle, url: location.pathname },
      ];
      injectScript('ld-breadcrumb', getBreadcrumbSchema(breadcrumbItems));
      injectScript(
        'ld-article',
        getArticleSchema(
          articleTitle,
          articleDescription || '',
          location.pathname,
          articleDate || '',
          categoryName
        )
      );
    }

    if (type === 'about') {
      const breadcrumbItems = [
        { name: '首页', url: '/' },
        { name: '关于我们', url: '/about' },
      ];
      injectScript('ld-breadcrumb', getBreadcrumbSchema(breadcrumbItems));
    }

    return () => {
      ['ld-org', 'ld-website', 'ld-webpage', 'ld-breadcrumb', 'ld-article', 'ld-faq'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [location.pathname, type, categoryName, articleTitle, articleDescription, articleDate, articleCategory, faqs]);

  return null;
}